<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->
@if ((msel?.integrationStatus || integrationRequested) && !integrationDismissed) {
<app-integration-status [msel]="msel" (closed)="dismissIntegrationStatus()"></app-integration-status>
} @else {
<div class="frame">
  <mat-tab-group mat-align-tabs="start" class="tab-group" [selectedIndex]="currentTabIndex"
    (selectedIndexChange)="onTabIndexChange($event)">
    >
    <mat-tab label="Config" class="tab">
      <div class="edit-buttons">
        <span class="edit-buttons-span">
          <button mat-icon-button (click)="saveChanges()" style="outline: none;" title="Save Changes"
            [disabled]="!isChanged || !isMselValid()">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-content-save-outline"></mat-icon>
          </button>
          <button mat-icon-button (click)="cancelChanges()" style="outline: none;" title="Cancel Changes"
            [disabled]="!isChanged">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-close-circle-outline"></mat-icon>
          </button>
        </span>
        <span class="change-span">
          @if (isChanged) {
          <mat-label>Changes have not been saved!</mat-label>
          }
        </span>
        <span class="export-import-span">
          <button mat-icon-button [matMenuTriggerFor]="downloadMenu" title="Download" style="outline: none;">
            <mat-icon class="mdi-18px" fontIcon="mdi-download"></mat-icon>
          </button>
          <mat-menu #downloadMenu="matMenu" [overlapTrigger]="false">
            <button mat-menu-item (click)="downloadJsonFile()" title="Download json file">Download json file</button>
            <button mat-menu-item (click)="downloadXlsxFile()" title="Download xlsx file">Download xlsx file</button>
          </mat-menu>
        </span>
        <span class="delete-span">
          @if (canManageMsel()) {
          <span [title]="getDeleteTooltip()">
            <button mat-icon-button (click)="deleteMsel()"
              [disabled]="msel && (msel.status === 'Deployed' || msel.isTemplate)">
              <mat-icon class="mdi-18px" fontIcon="mdi-trash-can-outline" alt="Delete"></mat-icon>
            </button>
          </span>
          }
        </span>
      </div>
      @if (msel) {
      <div class="scrolling-region">
        <div class="info-detail">
          <!-- MSEL Name -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Name</mat-label>
              <input type="text" matInput [(ngModel)]="msel.name" #mselName="ngModel" required pattern=".*\S.*"
                [errorStateMatcher]="showErrorWhenDirty" maxlength="70" (cut)="isChanged = true"
                (change)="isChanged = true" (paste)="isChanged = true" (keypress)="isChanged = true"
                [disabled]="!canEditMsel()" />
              @if (mselName.hasError('required') || mselName.hasError('pattern')) {
                <mat-error>
                  Name is <strong>required</strong>
                </mat-error>
              }
              <mat-hint align="end">{{ msel.name?.length || 0 }} / 70 characters</mat-hint>
            </mat-form-field>
          </div>
          <!-- MSEL Description -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>Description</mat-label>
              <input type="text" matInput placeholder="Description" [(ngModel)]="msel.description" maxlength="600"
                (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                (keypress)="isChanged = true" [disabled]="!canEditMsel()" />
              <mat-hint align="end">{{ msel.description?.length || 0 }} / 600 characters</mat-hint>
            </mat-form-field>
          </div>
          <!-- Is a Template -->
          <div class="single-detail center-self">
            <div class="sub-detail center-self">
              <mat-checkbox [(ngModel)]="msel.isTemplate" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                (keypress)="isChanged = true" title="Mark this MSEL as a reusable template for creating new MSELs">Is a
                Template</mat-checkbox>
            </div>
          </div>
          <!-- MSEL Start Time -->
          <div class="single-checkbox">
            <mat-checkbox [checked]="msel.startTime?.valueOf() > 0" [disabled]="!canEditMsel()"
              (change)="toggleStartTime($event)" title="Schedule a specific start date and time for this MSEL">Set a
              Start Time
            </mat-checkbox>
          </div>
          @if (hasStartTime()) {
          <div class="single-detail center-self">
            <mat-form-field class="datetime-field">
              <mat-label>Start Date / Time</mat-label>
              <input matInput [ngxMatDatetimePicker]="picker" [(ngModel)]="msel.startTime"
                (dateChange)="isChanged = true" (dateInput)="isChanged = true" style="display: none;"
                [disabled]="!canEditMsel()">
              <!-- <datetime-picker> -->
              <span (click)="canEditMsel() && picker.open()">
                {{msel.startTime | date: 'dd MMM yyyy HH:mm'}} {{ msel.startTime | timezoneAbbr }}
              </span>
              <mat-datepicker-toggle matSuffix title="Choose Date/Time" [for]="picker" [disabled]="!canEditMsel()">
              </mat-datepicker-toggle>
              <ngx-mat-datetime-picker #picker [showSpinners]=true [showSeconds]=false [stepHour]=1 [stepMinute]=5
                [stepSecond]=1 [touchUi]=false [enableMeridian]=true [disableMinute]=false [hideTime]=false
                [color]="'warn'">
                <ngx-mat-datepicker-actions>
                  <button mat-stroked-button ngxMatDatepickerApply>Save</button>
                  <button mat-stroked-button ngxMatDatepickerCancel>Cancel</button>
                </ngx-mat-datepicker-actions>
              </ngx-mat-datetime-picker>
            </mat-form-field>
          </div>
          }
          <!-- MSEL End/Duration -->
          <app-duration-edit [startTime]="msel.startTime" [(durationSeconds)]="msel.durationSeconds"
            [timeName]="'End Date / Time'" [postScript]="'duration'" [canEdit]="canEditMsel()"
            (change)="isChanged = true;"></app-duration-edit>
          <!-- Integration Section -->
          <div class="integration-div">
            <div class="integration-header">
              <b>Applications to Integrate</b>
              @if ((msel.usePlayer || msel.useCite || msel.useGallery || msel.useSteamfitter || msel.useCaster) && !msel.isTemplate) {
              <div class="integration-actions">
                @if ((!msel.playerViewId &&
                !msel.citeEvaluationId &&
                !msel.galleryCollectionId &&
                !msel.steamfitterScenarioId &&
                !msel.casterWorkspaceId &&
                !msel.isTemplate) ||
                pushStatus) {
                <button (click)="pushIntegrations()" title="Push Integrations" class="thin-button" [disabled]="!canManageMsel() ||
                              (msel.useGallery && galleryToDo()) ||
                              (msel.useCite && !msel.citeScoringModelId) ||
                              (msel.useCite && hasCiteTeamsWithoutType()) ||
                              !!casterWarningMessage() ||
                              isBusy || isChanged || (pushStatus && pushStatus.startsWith('ERROR'))">@if (pushStatus &&
                  !pushStatus.startsWith('ERROR')) {
                  <span>{{ pushStatus }} ...</span>
                  }@if (!pushStatus || pushStatus.startsWith('ERROR')) {
                  <span>Push Integrations</span>
                  }</button>
                }
                @if ((msel.playerViewId ||
                msel.citeEvaluationId ||
                msel.galleryCollectionId ||
                msel.steamfitterScenarioId ||
                msel.casterWorkspaceId) &&
                !pushStatus) {
                <button (click)="pullIntegrations()" title="Remove Integrations" class="thin-button"
                  [disabled]="!canManageMsel() || isChanged">Remove
                  Integrations</button>
                }
              </div>
              @if (pushStatus && pushStatus.startsWith('ERROR')) {
              <div class="integration-error"
                style="color: red; margin-top: 8px; padding: 8px; background-color: #ffebee; border-radius: 4px;">
                <strong>Error:</strong> {{ pushStatus.substring(7) }}
              </div>
              }
              }
            </div>

            <!-- Player Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.usePlayer" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">Player</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.usePlayer && msel.playerViewId) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">View:</span>
                  <span class="integration-guid" [title]="msel.playerViewId">{{ msel.playerViewId }}</span>
                  @if (playerViewName) {
                  <span class="integration-name">{{ playerViewName }}</span>
                  }
                  <a [href]="getPlayerViewUrl()" target="_blank" class="integration-link" title="Open in Player">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>

            <!-- Gallery Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.useGallery" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">Gallery</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.useGallery && msel.galleryExhibitId) {
              <div class="integration-details">
                @if (msel.galleryCollectionId) {
                <div class="integration-field">
                  <span class="integration-label">Collection:</span>
                  <span class="integration-guid" [title]="msel.galleryCollectionId">{{ msel.galleryCollectionId
                    }}</span>
                  @if (galleryCollectionName) {
                  <span class="integration-name">{{ galleryCollectionName }}</span>
                  }
                </div>
                }
                <div class="integration-field">
                  <span class="integration-label">Exhibit:</span>
                  <span class="integration-guid" [title]="msel.galleryExhibitId">{{ msel.galleryExhibitId }}</span>
                  @if (galleryExhibitName) {
                  <span class="integration-name">{{ galleryExhibitName }}</span>
                  }
                  <a [href]="getGalleryExhibitUrl()" target="_blank" class="integration-link" title="Open in Gallery">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>
            @if (galleryWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ galleryWarningMessage() }}</span>
            </div>
            }

            <!-- CITE Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [(ngModel)]="msel.useCite" [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                  (keypress)="isChanged = true">CITE</mat-checkbox>
              </div>
              @if (msel.useCite) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">Scoring Model:</span>
                  @if (scoringModelList.length > 0 && msel.status !== 'Deployed' && canEditMsel()) {
                  <mat-form-field class="integration-select">
                    <mat-select [(ngModel)]="msel.citeScoringModelId" (cut)="isChanged = true"
                      (selectionChange)="isChanged = true; updateCiteScoringModelName()" (paste)="isChanged = true"
                      (keypress)="isChanged = true">
                      <mat-option [value]=""></mat-option>
                      @for (scoringModel of scoringModelList; track scoringModel) {
                      <mat-option [value]="scoringModel.id">
                        {{ scoringModel.description }}
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  }
                  @if (scoringModelList.length === 0 || msel.status === 'Deployed' || !canEditMsel()) {
                  @if (msel.citeScoringModelId) {
                  <span class="integration-guid" [title]="msel.citeScoringModelId">{{ msel.citeScoringModelId }}</span>
                  @if (citeScoringModelName) {
                  <span class="integration-name">{{ citeScoringModelName }}</span>
                  }
                  }
                  }
                </div>
                @if (!msel.isTemplate && msel.citeEvaluationId) {
                <div class="integration-field">
                  <span class="integration-label">Evaluation:</span>
                  <span class="integration-guid" [title]="msel.citeEvaluationId">{{ msel.citeEvaluationId }}</span>
                  @if (citeEvaluationName) {
                  <span class="integration-name">{{ citeEvaluationName }}</span>
                  }
                  <a [href]="getCiteEvaluationUrl()" target="_blank" class="integration-link" title="Open in CITE">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
                }
              </div>
              }
            </div>
            @if (citeWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ citeWarningMessage() }}</span>
            </div>
            }

            <!-- Steamfitter Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  [(ngModel)]="msel.useSteamfitter" (cut)="isChanged = true" (change)="isChanged = true"
                  (paste)="isChanged = true" (keypress)="isChanged = true">Steamfitter</mat-checkbox>
              </div>
              @if (!msel.isTemplate && msel.useSteamfitter && msel.steamfitterScenarioId) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">Scenario:</span>
                  <span class="integration-guid" [title]="msel.steamfitterScenarioId">{{ msel.steamfitterScenarioId
                    }}</span>
                  @if (steamfitterScenarioName) {
                  <span class="integration-name">{{ steamfitterScenarioName }}</span>
                  }
                  <a [href]="getSteamfitterScenarioUrl()" target="_blank" class="integration-link"
                    title="Open in Steamfitter">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                </div>
              </div>
              }
            </div>

            <!-- Caster Integration -->
            <div class="integration-row">
              <div class="integration-checkbox-cell">
                <mat-checkbox [disabled]="msel.status === 'Deployed' || !canEditMsel()"
                  [(ngModel)]="msel.useCaster" (cut)="isChanged = true" (change)="isChanged = true; $event.checked && loadCasterDirectories()"
                  (paste)="isChanged = true" (keypress)="isChanged = true">Caster</mat-checkbox>
              </div>
              @if (msel.useCaster) {
              <div class="integration-details">
                <div class="integration-field">
                  <span class="integration-label">Directory:</span>
                  @if (casterDirectoryList.length > 0 && msel.status !== 'Deployed' && canEditMsel()) {
                  <mat-form-field class="integration-select">
                    <mat-select [(ngModel)]="msel.casterDirectoryId" (selectionChange)="isChanged = true">
                      <mat-option [value]="null"></mat-option>
                      @for (directory of casterDirectoryList; track directory.id) {
                      <mat-option [value]="directory.id">
                        {{ casterDirectoryLabel(directory) }}
                      </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  }
                  @if (casterDirectoryList.length === 0 || msel.status === 'Deployed' || !canEditMsel()) {
                  @if (msel.casterDirectoryId) {
                  <span class="integration-guid" [title]="msel.casterDirectoryId">{{ msel.casterDirectoryId }}</span>
                  @if (selectedCasterDirectory || casterDirectoryName) {
                  <span class="integration-name">{{ selectedCasterDirectory ? casterDirectoryLabel(selectedCasterDirectory) : casterDirectoryName }}</span>
                  }
                  }
                  }
                </div>
                @if (!msel.isTemplate && msel.casterWorkspaceId) {
                <div class="integration-field">
                  <span class="integration-label">Workspace:</span>
                  <span class="integration-guid" [title]="msel.casterWorkspaceId">{{ msel.casterWorkspaceId }}</span>
                  @if (casterWorkspaceName) {
                  <span class="integration-name">{{ casterWorkspaceName }}</span>
                  }
                  @if (getCasterProjectUrl()) {
                  <a [href]="getCasterProjectUrl()" target="_blank" class="integration-link" title="Open in Caster">
                    <mat-icon class="mdi-18px" fontIcon="mdi-open-in-new"></mat-icon>
                  </a>
                  }
                </div>
                }
              </div>
              }
            </div>
            @if (msel.useCaster && msel.status !== 'Deployed' && casterDirectoryError) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ casterDirectoryError }}</span>
            </div>
            }
            @if (casterWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ casterWarningMessage() }}</span>
            </div>
            }
            @if (msel.status !== 'Deployed' && casterVmAppWarningMessage()) {
            <div class="integration-warning">
              <span class="gallery-warning">{{ casterVmAppWarningMessage() }}</span>
            </div>
            }
          </div>
          <!-- Status -->
          <div class="single-detail center-self">
            <mat-form-field class="full-width">
              <mat-label>MSEL Status</mat-label>
              <mat-select [(ngModel)]="msel.status" [disabled]="isPipelineStatus() || !canEditMsel()"
                (cut)="isChanged = true" (selectionChange)="isChanged = true" (change)="isChanged = true"
                (paste)="isChanged = true" (keypress)="isChanged = true">
                @for (item of statusOptions(); track item) {
                <mat-option [value]="item">
                  {{ item }}
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <!-- Exercise View URL -->
          <div class="url-form-field">
            <div class="editor-label">
              Exercise View URL
              <div class="field-hint">
                Use this URL for direct access to view the Exercise MSEL
              </div>
            </div>
            <div class="url-button-link">
              <button mat-icon-button ngxClipboard [cbContent]="viewUrl" (click)="$event.stopPropagation()"
                title="Copy Exercise View URL:  {{ viewUrl }}">
                <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
              </button>
              <a target="_blank" href="{{ viewUrl }}">{{ viewUrl }}</a>
            </div>
          </div>
          <!-- MSEL Starter URL -->
          <div class="url-form-field">
            <div class="editor-label">
              MSEL Starter URL
              <div class="field-hint">
                Use this URL for direct access to edit the Scenario Events
              </div>
            </div>
            <div class="url-button-link">
              <button mat-icon-button ngxClipboard [cbContent]="starterUrl" (click)="$event.stopPropagation()"
                title="Copy MSEL Starter URL:  {{ starterUrl }}">
                <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
              </button>
              <a target="_blank" href="{{ starterUrl }}">{{ starterUrl }}</a>
            </div>
          </div>
          <!-- Assessor View URL -->
          <div class="url-form-field">
            <div class="editor-label">
              Assessor View URL
              <div class="field-hint">
                Use this URL for direct access to the Assessor View
              </div>
            </div>
            <div class="url-button-link">
              <button mat-icon-button ngxClipboard [cbContent]="assessorUrl" (click)="$event.stopPropagation()"
                title="Copy Assessor View URL:  {{ assessorUrl }}">
                <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
              </button>
              <a target="_blank" href="{{ assessorUrl }}">{{ assessorUrl }}</a>
            </div>
          </div>
          <!-- Competencies summary -->
          <div class="competency-section">
            <div class="section-header">
              <mat-icon class="mdi-18px" fontIcon="mdi-certificate-outline"
                style="color: var(--mat-sys-primary); vertical-align: middle; margin-right: 4px;"></mat-icon>
              <b>Competencies</b>
              @if (competencyFrameworkNames.length > 0) {
              <span
                style="margin-left: 8px; font-weight: normal; color: var(--mat-sys-on-surface-variant); font-size: 13px;">
                ({{ competencyFrameworkNames.join(', ') }})
              </span>
              }
            </div>
            <div class="competency-panels-row">
              @if (workRoleCompetencies.length > 0) {
              <mat-expansion-panel class="competency-expansion">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <mat-icon class="mdi-18px" fontIcon="mdi-account-hard-hat"
                      style="color: var(--mat-sys-primary); margin-right: 6px;"></mat-icon>
                    Work Roles ({{ workRoleCompetencies.length }})
                  </mat-panel-title>
                </mat-expansion-panel-header>
                <div class="competency-list">
                  @for (c of workRoleCompetencies; track c.id) {
                  <div class="competency-list-item">
                    <span class="competency-list-id" [matTooltip]="getCompetencyTooltip(c)"
                      matTooltipPosition="right">{{ c.idNumber }}</span>
                  </div>
                  }
                </div>
              </mat-expansion-panel>
              }
              @if (nonWorkRoleCompetencies.length > 0) {
              <mat-expansion-panel class="competency-expansion">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <mat-icon class="mdi-18px" fontIcon="mdi-certificate-outline"
                      style="color: var(--mat-sys-primary); margin-right: 6px;"></mat-icon>
                    Competencies ({{ nonWorkRoleCompetencies.length }})
                  </mat-panel-title>
                </mat-expansion-panel-header>
                <div class="competency-list">
                  @for (c of nonWorkRoleCompetencies; track c.id) {
                  <div class="competency-list-item">
                    <span class="competency-list-id" [matTooltip]="getCompetencyTooltip(c)"
                      matTooltipPosition="right">{{ c.idNumber }}</span>
                  </div>
                  }
                </div>
              </mat-expansion-panel>
              }
            </div>
            <div class="competency-hint">Manage on the Competencies tab</div>
          </div>
          <!-- LMT Metadata -->
          <div class="section-header">
            <mat-icon class="mdi-18px" fontIcon="mdi-tag-outline"
              style="color: var(--mat-sys-primary); vertical-align: middle; margin-right: 4px;"></mat-icon>
            <b>Metadata</b>
          </div>
          <div class="lmt-dropdown-row">
            <mat-form-field class="lmt-dropdown-width"
              matTooltip="Beginner, Intermediate, or Advanced — maps to lrmi:educationalLevel"
              matTooltipShowDelay="800">
              <mat-label>Difficulty Rating</mat-label>
              <mat-select [(ngModel)]="msel.educationalLevel" [disabled]="!canEditMsel()"
                (selectionChange)="isChanged = true">
                <mat-option [value]="null"></mat-option>
                @for (level of educationalLevels; track level) {
                <mat-option [value]="level">{{ level }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field class="lmt-dropdown-width"
              matTooltip="Assessment, Instruction, or Professional Support — maps to lrmi:educationalUse"
              matTooltipShowDelay="800">
              <mat-label>Exercise Purpose</mat-label>
              <mat-select [(ngModel)]="msel.educationalUse" [disabled]="!canEditMsel()"
                (selectionChange)="isChanged = true">
                <mat-option [value]="null"></mat-option>
                @for (use of educationalUses; track use) {
                <mat-option [value]="use">{{ use }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field class="lmt-dropdown-width"
              matTooltip="Online, Onsite, or Blended delivery mode — maps to schema:courseMode"
              matTooltipShowDelay="800">
              <mat-label>Exercise Mode</mat-label>
              <mat-select [(ngModel)]="msel.courseMode" [disabled]="!canEditMsel()"
                (selectionChange)="isChanged = true">
                <mat-option [value]="null"></mat-option>
                @for (mode of courseModes; track mode) {
                <mat-option [value]="mode">{{ mode }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="single-detail center-self">
            <mat-form-field class="full-width"
              matTooltip="Topic areas covered by this exercise — maps to dcterms:subject" matTooltipShowDelay="800">
              <mat-label>Subject</mat-label>
              <input type="text" matInput placeholder="e.g., incident response, network defense"
                [(ngModel)]="msel.subject" (cut)="isChanged = true" (change)="isChanged = true"
                (paste)="isChanged = true" (keypress)="isChanged = true" [disabled]="!canEditMsel()" />
            </mat-form-field>
          </div>
          <div class="single-detail center-self">
            <mat-form-field class="full-width"
              matTooltip="Searchable tags for catalog discovery — maps to schema:keywords" matTooltipShowDelay="800">
              <mat-label>Keywords</mat-label>
              <input type="text" matInput placeholder="Searchable tags" [(ngModel)]="msel.keywords"
                (cut)="isChanged = true" (change)="isChanged = true" (paste)="isChanged = true"
                (keypress)="isChanged = true" [disabled]="!canEditMsel()" />
            </mat-form-field>
          </div>
          <!-- row meta data -->
          @if (canManageMsel()) {
          <div class="single-detail center-self">
            <mat-form-field class="meta-field-width"
              title="A numeric value that defines the height of the header row when this MSEL is exported as an xlsx file">
              <mat-label>Header Row Metadata (Height)</mat-label>
              <input type="text" matInput [(ngModel)]="msel.headerRowMetadata" (cut)="isChanged = true"
                (change)="isChanged = true" (paste)="isChanged = true" (keypress)="isChanged = true"
                title="An integer value that defines the height of the header row when this MSEL is exported as an xlsx file" />
            </mat-form-field>
          </div>
          }
          <div class="lmt-dropdown-row">
            <mat-form-field class="meta-field-width">
              <mat-label>Date Created</mat-label>
              <input type="text" matInput value="{{ msel.dateCreated | date: 'yyyy-MM-dd' }}" disabled />
            </mat-form-field>
            <mat-form-field class="meta-field-width">
              <mat-label>Created By</mat-label>
              <input type="text" matInput value="{{ creatorName }}" disabled />
            </mat-form-field>
          </div>
        </div>
      </div>
      }
    </mat-tab>
    @for (page of mselPages; track page.id) {
    <mat-tab label="{{ page.name }}" class="tab">
      <!-- page content -->
      <div class="edit-buttons">
        @if (editingPageId !== page.id) {
        <span>
          @if (canManageMsel()) {
          <button mat-icon-button (click)="requestPageEdit(page.id)" style="outline: none;" title="Edit Page">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          }
          <button mat-icon-button (click)="deletePage(page); $event.stopPropagation()" style="outline: none;"
            title="Delete {{ page.name }}">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-trash-can-outline"></mat-icon>
          </button>
          <button mat-icon-button ngxClipboard [cbContent]="basePageUrl + page.id" (click)="$event.stopPropagation()"
            title="Copy page URL">
            <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
          </button>
          <button mat-icon-button (click)="openContent(page.id)" title="Open page in new tab" class="far-right-button">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-open-in-new"></mat-icon>
          </button>
          <span
            [title]="page.allCanView ? 'All MSEL users can view this page' : 'Only MSEL contributors can view this page'">
            <button mat-icon-button disabled>
              <mat-icon class="mdi-24px" [fontIcon]="page.allCanView ? 'mdi-eye' : 'mdi-eye-off'"
                style="color: var(--mat-sys-primary)"></mat-icon>
            </button>
          </span>
          <span [title]="page.includeInPlaybook ? 'Included in playbook' : 'Not included in playbook'">
            <button mat-icon-button disabled>
              <mat-icon class="mdi-24px"
                [fontIcon]="page.includeInPlaybook ? 'mdi-file-document-check' : 'mdi-file-document-remove'"
                style="color: var(--mat-sys-primary)"></mat-icon>
            </button>
          </span>
        </span>
        }
        @if (editingPageId === page.id) {
        <span>
          <button mat-icon-button (click)="saveMselPageEdits()" style="outline: none;" title="Save Changes">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-content-save-outline"></mat-icon>
          </button>
          <button mat-icon-button (click)="cancelMselPageEdits()" style="outline: none;" title="Cancel Changes">
            <mat-icon class="mdi-24px self-center" fontIcon="mdi-close-circle-outline"></mat-icon>
          </button>
          @if (hasUnsavedPageChanges()) {
          <mat-label class="unsaved-warning">Changes have not been saved!</mat-label>
          }
        </span>
        }
      </div>
      @if (msel) {
      <div class="page-content-region">
        @if (editingPageId === page.id) {
        <mat-form-field class="full-width">
          <input type="text" matInput placeholder="Page Name" [(ngModel)]="changedMselPage.name"
            [disabled]="editingPageId !== page.id" />
        </mat-form-field>
        }
        <div class="all-can-view-div">
          @if (editingPageId === page.id) {
          <mat-checkbox [(ngModel)]="changedMselPage.allCanView" title="All MSEL users can view this page">All MSEL
            users can view this page</mat-checkbox>
          <mat-checkbox [(ngModel)]="changedMselPage.includeInPlaybook"
            title="Include this page when printing the playbook">Include in playbook</mat-checkbox>
          }
        </div>
        <div>
          @if (editingPageId === page.id) {
          <angular-editor [placeholder]="'Content'" [(ngModel)]="changedMselPage.content"
            [config]="editorConfig"></angular-editor>
          } @else {
          <angular-editor [placeholder]="'Content'" [ngModel]="page.content" [config]="viewConfig"></angular-editor>
          }
        </div>
      </div>
      }
    </mat-tab>
    }
    @if (canManageMsel()) {
    <mat-tab>
      <ng-template mat-tab-label>
        <mat-icon class="mdi-24px" fontIcon="mdi-plus-circle" title="Add MSEL Notes Page"></mat-icon>
        Add Page
      </ng-template>
      <div class="page-content-region">
        <p>Click this tab to create a new page.</p>
      </div>
    </mat-tab>
    }
  </mat-tab-group>
</div>
}
